import type { ProfileLink } from "../lib/profileLinks";

import { api } from "./client";
import type { EffectiveReferralTerms, ReferralTerms } from "./events";

export type { ReferralTerms };

export type CreatorRow = {
  id: string;
  created_at: string | null;
  name: string;
  email: string;
  status: string;
  origin: string | null;
  work_count: number;
  verified: boolean;
  creator_type: string | null;
  tagline: string | null;
  description: string | null;
  link: string | null;
  origin_event_name: string | null;
};

export type CreatorSortField =
  | "created_at"
  | "name"
  | "email"
  | "status"
  | "origin"
  | "work_count"
  | "verified";

export type SortDir = "asc" | "desc";

export type CreatorListResponse = {
  creators: CreatorRow[];
  page: number;
  page_size: number;
  total: number;
  sort: CreatorSortField;
  dir: SortDir;
};

export type CreatorListParams = {
  page: number;
  page_size?: number;
  /** Free-text substring across name/email/profile (creator typeahead). */
  q?: string;
  status?: string;
  origin?: string;
  /** "with" | "without" — undefined = no filter (show all) */
  verified?: "with" | "without";
  created_after?: string;
  created_before?: string;
  expr?: string;
  sort?: CreatorSortField;
  dir?: SortDir;
};

export type CreatorStats = {
  by_status: Record<string, number>;
  added: { day: number; week: number; month: number };
};

// One creator_profile row, in full. The CreatorDetail accordion renders these
// directly; the flattened `tagline`/`bio`/`custom_link`/... on CreatorDetail are
// the SAME columns copied off the primary profile, and still drive edit mode.
export type CreatorProfileRow = {
  id: string;
  profile_name: string;
  custom_link: string | null;
  profile_picture: string | null;
  profile_picture_url: string | null;
  profile_types: string[];
  tagline: string | null;
  bio: string | null;
  description: string | null;
  profile_location: string | null;
  // Legacy: string[] whose elements are JSON strings (customer-app) or bare
  // URLs (support CSV import). Superseded by social_links/links; still served
  // and accepted until every consumer reads the normalized pair. See
  // plans/2026-08-07-profile-links-normalization-design.md.
  socials: string[];
  other_links: string[];
  social_links?: ProfileLink[];
  links?: ProfileLink[];
  creative_styles: string[];
  created_at: string | null;
  updated_at: string | null;
};

export type CreatorPIIDetail = {
  name: string | null;
  name_searchable: boolean | null;
  email: string | null;
  email_searchable: boolean | null;
};

export type CreatorDetail = {
  id: string;
  created_at: string | null;
  updated_at: string | null;
  status: string;
  profile_types: string[];
  tagline: string | null;
  bio: string | null;
  description: string | null;
  profile_location: string | null;
  // Legacy: string[] whose elements are JSON strings (customer-app) or bare
  // URLs (support CSV import). Superseded by social_links/links; still served
  // and accepted until every consumer reads the normalized pair. See
  // plans/2026-08-07-profile-links-normalization-design.md.
  socials: string[];
  other_links: string[];
  social_links?: ProfileLink[];
  links?: ProfileLink[];
  creative_styles: string[];
  custom_link: string | null;
  profile_picture: string | null;
  profile_picture_url: string | null;
  origin: string | null;
  pii: CreatorPIIDetail;
  profiles: CreatorProfileRow[];
  work_count: number;
  credits: number;
  origin_event_id: string | null;
  origin_event_name: string | null;
  sign_in_method: string | null;
  verified: boolean;
  referral_terms: ReferralTerms | null;
  // Read-only: what the cascade resolves to for this creator. Never PATCHed —
  // `referral_terms` above is the only writable scope.
  referral_terms_effective: EffectiveReferralTerms | null;
};

export type CreatorPatch = Partial<{
  status: string;
  origin: string | null;
  credits: number;
  tagline: string | null;
  bio: string | null;
  description: string | null;
  profile_location: string | null;
  custom_link: string | null;
  profile_picture: string | null;
  profile_types: string[];
  // Legacy: string[] whose elements are JSON strings (customer-app) or bare
  // URLs (support CSV import). Superseded by social_links/links; still served
  // and accepted until every consumer reads the normalized pair. See
  // plans/2026-08-07-profile-links-normalization-design.md.
  socials: string[];
  other_links: string[];
  social_links?: ProfileLink[];
  links?: ProfileLink[];
  creative_styles: string[];
  pii: Partial<{
    name: string;
    name_searchable: boolean;
    email: string;
    email_searchable: boolean;
  }>;
  profiles: CreatorProfileRow[];
  origin_event_id: string | null;
  verified: boolean;
  referral_terms: ReferralTerms | null;
}>;

// Fields the bulk editor may set across many creators at once — a deliberate
// subset of CreatorPatch (no per-creator free text like name/email/bio).
export type CreatorBulkChanges = Partial<{
  status: string;
  origin: string | null;
  pii: Partial<{ name_searchable: boolean; email_searchable: boolean }>;
  origin_event_id: string | null;
}>;

export const CreatorsApi = {
  list(params: CreatorListParams) {
    return api
      .get<CreatorListResponse>("/creators", {
        params: {
          page: params.page,
          page_size: params.page_size ?? 40,
          q: params.q || undefined,
          status: params.status || undefined,
          origin: params.origin || undefined,
          verified: params.verified || undefined,
          created_after: params.created_after || undefined,
          created_before: params.created_before || undefined,
          expr: params.expr || undefined,
          sort: params.sort || undefined,
          dir: params.dir || undefined,
        },
      })
      .then((r) => r.data);
  },
  origins() {
    return api.get<{ origins: string[] }>("/creators/origins").then((r) => r.data.origins);
  },
  stats() {
    return api.get<CreatorStats>("/creators/stats").then((r) => r.data);
  },
  get(id: string) {
    return api.get<CreatorDetail>(`/creators/${id}`).then((r) => r.data);
  },
  update(id: string, patch: CreatorPatch, audit: { reason: string }) {
    return api
      .patch<CreatorDetail>(`/creators/${id}`, { ...patch, ...audit })
      .then((r) => r.data);
  },
  works(id: string) {
    return api
      .get<{ works: CreatorWorkRow[]; total: number }>(`/creators/${id}/works`)
      .then((r) => r.data);
  },
  bulkDelete(ids: string[]) {
    return api
      .delete<{ deleted: string[]; refused: string[] }>("/creators", {
        data: { ids },
      })
      .then((r) => r.data);
  },
  bulkUpdate(ids: string[], changes: CreatorBulkChanges, audit: { reason: string }) {
    return api
      .patch<{ updated: string[]; refused: string[] }>("/creators", {
        ids,
        changes,
        reason: audit.reason,
      })
      .then((r) => r.data);
  },
  getAvatarUploadUrl(id: string, contentType: string) {
    return api
      .post<{ uploadUrl: string; key: string; expiresIn: number }>(
        `/creators/${id}/profile-picture/upload-url`,
        { contentType },
      )
      .then((r) => r.data);
  },
  // Attaching/clearing the avatar is applied via update() as the
  // `profile_picture` patch field (held until Save, gated by reason, audited).
  create(body: CreatorCreate) {
    return api.post<CreatorDetail>("/creators", body).then((r) => r.data);
  },
  bulkImport(body: {
    origin: string | null;
    status: "PROSPECTIVE" | "IMPORTED" | "VOUCHED" | "ACTIVE";
    csv: string;
  }) {
    return api
      .post<BulkImportResult>("/creators/bulk-import", body)
      .then((r) => r.data);
  },
};

export type CreatorCreate = {
  name: string;
  email: string;
  status: "PROSPECTIVE" | "VOUCHED" | "ACTIVE";
  tagline?: string | null;
  creator_type?: string | null;
  origin?: string | null;
};

export type BulkImportResult = {
  imported: { id: string; email: string }[];
  updated: { id: string; email: string }[];
  skipped: { row: number; email: string; reason: string }[];
  errors: { row: number; reason: string; email?: string }[];
};

export type CreatorWorkRow = {
  id: string;
  created_at: string | null;
  title: string;
  type: string;
  format: string;
  status: string | null;
  creator_profile_id: string | null;
  profile_name: string | null;
};

// Mirrors orm.CreatorStatus — kept in sync manually for the filter dropdown.
export const CREATOR_STATUSES = [
  "SETUP_PAYMENT",
  "SETUP_ID_VERIFY",
  "SETUP_ATTESTATION",
  "SETUP_SCHEDULE_INTERVIEW",
  "SETUP_INTERVIEW",
  "SETUP_ID_SUPPORT",
  "ACTIVE",
  "VOUCHED",
  "REJECTED",
  "SUSPENDED",
  "CLOSED",
  "IMPORTED",
  "PROSPECTIVE",
] as const;
